import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { QueryOptions } from '@shared/fetch-utils';
import { buildUrl } from '@shared/utils';
import { useMPEFetch } from '@retail/app/hooks';
import { mpeApi } from '@retail/mgpris/config';
import { AssortmentPricesDTO, AssortmentWithCountDTO } from '@retail/my-assortment/types';

export interface Props extends QueryOptions {
  assortmentId: number;
  supplierNumber?: number;
  productGroupNumbers?: string[];
  discountGroupCodes?: string[];
  assortmentTags?: string[];
  query?: string;
  withAgreementPrices?: boolean;
}

export function useFetchAssortmentPrices({
  assortmentId,
  supplierNumber,
  productGroupNumbers,
  discountGroupCodes,
  assortmentTags,
  query,
  withAgreementPrices,
  ...queryOptions
}: Props) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<AssortmentWithCountDTO<AssortmentPricesDTO>>({
    method: 'GET',
    queryKey: [
      'assortment-prices',
      assortmentId,
      supplierNumber,
      productGroupNumbers,
      discountGroupCodes,
      withAgreementPrices,
      assortmentTags,
      query,
    ],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortmentPrices.assortment.getFullLinkPath({ assortmentId }),
      searchParams: {
        supplierNumber,
        productGroupNumbers,
        discountGroupCodes,
        assortmentTags,
        query,
        withAgreementPrices,
      },
    }),
    ...queryOptions,
  });
}

export interface SupplierAssortmentPricesProps extends QueryOptions {
  assortmentId: number;
  supplierNumber: number;
  productGroupNumbers?: string[];
  discountGroupCodes?: string[];
  query?: string;
  withAgreementPrices?: boolean;
  inAssortment?: boolean;
}

export function useFetchAssortmentPricesForSupplier({
  assortmentId,
  supplierNumber,
  productGroupNumbers,
  discountGroupCodes,
  query,
  withAgreementPrices,
  inAssortment,
  ...queryOptions
}: SupplierAssortmentPricesProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<AssortmentWithCountDTO<AssortmentPricesDTO>>({
    method: 'GET',
    queryKey: [
      'assortment-prices-supplier',
      assortmentId,
      supplierNumber,
      productGroupNumbers,
      discountGroupCodes,
      query,
      withAgreementPrices,
      inAssortment,
    ],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortmentPrices.assortment.supplier.supplierNumber.getFullLinkPath({
        assortmentId,
        supplierNumber,
      }),
      searchParams: {
        productGroupNumbers,
        discountGroupCodes,
        query,
        withAgreementPrices,
        inAssortment,
      },
    }),
    ...queryOptions,
  });
}
